import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { ChevronDown, Trash2, Pencil, CreditCard, RotateCcw } from 'lucide-react';
import { useFloatingPosition } from '../hooks/useFloatingPosition';

interface RowActionsDropdownProps {
    onEdit: () => void;
    onDelete: () => void;
    onPay?: () => void;
    onRefund?: () => void;
    payLabel?: string;
    refundLabel?: string;
    editLabel?: string;
    deleteLabel?: string;
    disabled?: boolean;
    showPay?: boolean;
    showRefund?: boolean;
    showDelete?: boolean;
    isReadOnly?: boolean;
}

const RowActionsDropdown: React.FC<RowActionsDropdownProps> = ({
    onEdit,
    onDelete,
    onPay,
    onRefund,
    payLabel = 'Marcar como Pago',
    refundLabel = 'Estornar Pagamento',
    editLabel = 'Editar',
    deleteLabel = 'Excluir',
    disabled = false,
    showPay = true,
    showRefund = true,
    showDelete = true,
    isReadOnly = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const menuRef = useRef<HTMLDivElement | null>(null);
    const instanceIdRef = useRef<string>(Math.random().toString(36).slice(2));

    const { style, update } = useFloatingPosition({ getAnchor: () => buttonRef.current, menuWidth: 224, offset: 8, align: 'end' });

    useEffect(() => {
        if (!isOpen) return;
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            const insideButton = !!(buttonRef.current && buttonRef.current.contains(target));
            const insideMenu = !!(menuRef.current && menuRef.current.contains(target));
            if (!insideButton && !insideMenu) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside, true);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside, true);
        };
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        update();
    }, [isOpen, update]);

    // close other open dropdowns when opening this one
    useEffect(() => {
        if (!isOpen) return;
        const ev = new CustomEvent('dropdown:open', { detail: { id: instanceIdRef.current } } as any);
        document.dispatchEvent(ev);
    }, [isOpen]);

    useEffect(() => {
        const onOtherOpen = (e: Event) => {
            const detail = (e as CustomEvent).detail || {};
            if (detail.id !== instanceIdRef.current) setIsOpen(false);
        };
        document.addEventListener('dropdown:open', onOtherOpen as EventListener);
        return () => document.removeEventListener('dropdown:open', onOtherOpen as EventListener);
    }, []);

    useEffect(() => {
        if (!isOpen) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setIsOpen(false);
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [isOpen]);

    return (
        <div ref={containerRef} className="relative inline-block text-left">
            <button
                ref={buttonRef}
                onClick={() => setIsOpen(!isOpen)}
                disabled={disabled}
                className="btn-secondary flex items-center space-x-2 text-sm"
            >
                <span>Ações</span>
                <ChevronDown className="h-4 w-4" />
            </button>

            {isOpen && ReactDOM.createPortal(
                (
                    <div
                        ref={menuRef}
                        style={style}
                        className="z-50 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none border border-secondary-200"
                        role="menu"
                        aria-orientation="vertical"
                        aria-labelledby="menu-button"
                    >
                        <div className="py-1">
                            {onPay && showPay && (
                                <button
                                    onClick={() => { onPay(); setIsOpen(false); }}
                                    className="flex items-center w-full px-4 py-2 text-sm text-secondary-700 hover:bg-secondary-50"
                                >
                                    <CreditCard className="h-4 w-4 mr-3 text-green-600" />
                                    {payLabel}
                                </button>
                            )}

                            {onRefund && showRefund && (
                                <button
                                    onClick={() => { onRefund(); setIsOpen(false); }}
                                    className="flex items-center w-full px-4 py-2 text-sm text-secondary-700 hover:bg-secondary-50"
                                >
                                    <RotateCcw className="h-4 w-4 mr-3 text-orange-600" />
                                    {refundLabel}
                                </button>
                            )}

                            {((onPay && showPay) || (onRefund && showRefund)) && <div className="border-t border-secondary-200 my-1" />}

                            <button
                                onClick={() => { onEdit(); setIsOpen(false); }}
                                className="flex items-center w-full px-4 py-2 text-sm text-secondary-700 hover:bg-secondary-50"
                            >
                                <Pencil className="h-4 w-4 mr-3 text-secondary-700" />
                                {isReadOnly ? 'Visualizar' : editLabel}
                            </button>

                            {showDelete && (
                                <button
                                    onClick={() => { onDelete(); setIsOpen(false); }}
                                    className="flex items-center w-full px-4 py-2 text-sm text-red-700 hover:bg-red-50"
                                >
                                    <Trash2 className="h-4 w-4 mr-3 text-red-600" />
                                    {deleteLabel}
                                </button>
                            )}
                        </div>
                    </div>
                ),
                document.body
            )}
        </div>
    );
};

export default RowActionsDropdown;
